import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Slider } from "../ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Separator } from "../ui/separator";
import { toast } from "sonner";
import { Frame, Save, Eye, Palette, Layers, RotateCcw } from "lucide-react";
import { invalidateFrameConfigCache } from "../ui/FramedImage";
import { API } from "../../apiConfig";


// Frame color options
const FRAME_COLORS = [
  { id: "black", name: "Classic Black", color: "#1a1a1a" },
  { id: "white", name: "Gallery White", color: "#f5f5f5" },
  { id: "natural", name: "Natural Oak", color: "#c4a574" },
  { id: "walnut", name: "Dark Walnut", color: "#5c4033" },
  { id: "gold", name: "Antique Gold", color: "#d4af37" },
  { id: "silver", name: "Brushed Silver", color: "#c0c0c0" },
];

// Mat color options
const MAT_COLORS = [
  { id: "white", name: "Bright White", color: "#ffffff" },
  { id: "cream", name: "Museum Cream", color: "#f5f5dc" },
  { id: "black", name: "Deep Black", color: "#1a1a1a" },
  { id: "grey", name: "Warm Grey", color: "#9e9e9e" },
  { id: "none", name: "No Mat", color: null },
];

// Sample artwork for preview
const SAMPLE_ARTWORK = "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/7zcin9ey_8m10pyfqa.jpeg";

const FrameBuilderTab = ({ token }) => {
  const [config, setConfig] = useState({
    frameColor: "black",
    frameWidth: 30,
    matColor: "white",
    matWidth: 50,
    hasDoubleMat: false,
    innerMatColor: "cream",
    innerMatWidth: 10,
    glazingType: "standard",
  });
  
  const [defaultConfig, setDefaultConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewArtwork, setPreviewArtwork] = useState(SAMPLE_ARTWORK);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchDefaultConfig();
    fetchProducts();
  }, []);

  const fetchDefaultConfig = async () => {
    try {
      const response = await axios.get(`${API}/frames/default-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setConfig(response.data);
        setDefaultConfig(response.data);
      }
    } catch (error) {
      // No default config set yet
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=20`);
      setProducts(response.data.filter(p => p.images?.length > 0));
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const saveDefaultConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/frames/default-config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefaultConfig(config);
      // Invalidate the frontend cache so new frames are applied immediately
      invalidateFrameConfigCache();
      toast.success("Default frame configuration saved!");
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    if (defaultConfig) {
      setConfig(defaultConfig);
      toast.info("Reset to saved configuration");
    }
  };

  const getFrameColor = () => FRAME_COLORS.find(f => f.id === config.frameColor)?.color || "#1a1a1a";
  const getMatColor = () => MAT_COLORS.find(m => m.id === config.matColor)?.color;
  const getInnerMatColor = () => MAT_COLORS.find(m => m.id === config.innerMatColor)?.color;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Frame className="w-5 h-5" />
            Default Frame Builder
          </h2>
          <p className="text-sm text-muted-foreground">
            Design the default frame style for all art prints. Use this preview to get quotes for each size.
          </p>
        </div>
        <div className="flex gap-2">
          {defaultConfig && (
            <Button variant="outline" onClick={resetToDefault}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          )}
          <Button onClick={saveDefaultConfig} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save as Default"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Preview Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Live Preview
            </CardTitle>
            <CardDescription>
              How the framed artwork will appear
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Frame Preview */}
            <div className="flex items-center justify-center p-4">
              <div 
                className="relative transition-all duration-300"
                style={{
                  backgroundColor: getFrameColor(),
                  padding: `${config.frameWidth}px`,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                }}
              >
                {/* Outer Mat */}
                {config.matColor !== "none" && (
                  <div
                    className="transition-all duration-300"
                    style={{
                      backgroundColor: getMatColor(),
                      padding: `${config.matWidth}px`,
                    }}
                  >
                    {/* Inner Mat (Double Mat) */}
                    {config.hasDoubleMat && config.innerMatColor !== "none" ? (
                      <div
                        className="transition-all duration-300"
                        style={{
                          backgroundColor: getInnerMatColor(),
                          padding: `${config.innerMatWidth}px`,
                        }}
                      >
                        <img
                          src={previewArtwork}
                          alt="Preview artwork"
                          className="max-w-[280px] max-h-[280px] object-contain bg-white"
                          style={{ display: "block" }}
                        />
                      </div>
                    ) : (
                      <img
                        src={previewArtwork}
                        alt="Preview artwork"
                        className="max-w-[280px] max-h-[280px] object-contain bg-white"
                        style={{ display: "block" }}
                      />
                    )}
                  </div>
                )}
                {config.matColor === "none" && (
                  <img
                    src={previewArtwork}
                    alt="Preview artwork"
                    className="max-w-[280px] max-h-[280px] object-contain bg-white"
                    style={{ display: "block" }}
                  />
                )}
              </div>
            </div>

            {/* Artwork Selector */}
            <div className="mt-6">
              <Label className="text-sm text-muted-foreground">Preview with different artwork</Label>
              <Select 
                value={previewArtwork} 
                onValueChange={setPreviewArtwork}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select artwork" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SAMPLE_ARTWORK}>Sample Artwork</SelectItem>
                  {products.map(p => (
                    <SelectItem key={p.product_id} value={p.images[0]}>
                      {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Configuration Panel */}
        <div className="space-y-4">
          {/* Frame Options */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Frame className="w-4 h-4" />
                Frame Style
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Frame Color</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {FRAME_COLORS.map(frame => (
                    <button
                      key={frame.id}
                      onClick={() => setConfig({ ...config, frameColor: frame.id })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        config.frameColor === frame.id 
                          ? "border-primary ring-2 ring-primary/20" 
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div 
                        className="w-full h-6 rounded mb-2"
                        style={{ backgroundColor: frame.color }}
                      />
                      <span className="text-xs">{frame.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Frame Width</Label>
                  <span className="text-sm text-muted-foreground">{config.frameWidth}mm</span>
                </div>
                <Slider
                  value={[config.frameWidth]}
                  onValueChange={([v]) => setConfig({ ...config, frameWidth: v })}
                  min={15}
                  max={60}
                  step={5}
                />
              </div>
            </CardContent>
          </Card>

          {/* Mat Options */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Mat Board
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Mat Color</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {MAT_COLORS.map(mat => (
                    <button
                      key={mat.id}
                      onClick={() => setConfig({ ...config, matColor: mat.id })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        config.matColor === mat.id 
                          ? "border-primary ring-2 ring-primary/20" 
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div 
                        className="w-full h-6 rounded mb-2 border"
                        style={{ backgroundColor: mat.color || "transparent" }}
                      >
                        {!mat.color && <span className="text-xs text-muted-foreground">None</span>}
                      </div>
                      <span className="text-xs">{mat.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {config.matColor !== "none" && (
                <>
                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Mat Width</Label>
                      <span className="text-sm text-muted-foreground">{config.matWidth}mm</span>
                    </div>
                    <Slider
                      value={[config.matWidth]}
                      onValueChange={([v]) => setConfig({ ...config, matWidth: v })}
                      min={20}
                      max={100}
                      step={5}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Double Mat</Label>
                      <p className="text-xs text-muted-foreground">Add an inner accent mat</p>
                    </div>
                    <Switch
                      checked={config.hasDoubleMat}
                      onCheckedChange={(v) => setConfig({ ...config, hasDoubleMat: v })}
                    />
                  </div>

                  {config.hasDoubleMat && (
                    <>
                      <div>
                        <Label>Inner Mat Color</Label>
                        <Select 
                          value={config.innerMatColor} 
                          onValueChange={(v) => setConfig({ ...config, innerMatColor: v })}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MAT_COLORS.filter(m => m.id !== "none").map(mat => (
                              <SelectItem key={mat.id} value={mat.id}>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-4 h-4 rounded border"
                                    style={{ backgroundColor: mat.color }}
                                  />
                                  {mat.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <Label>Inner Mat Width</Label>
                          <span className="text-sm text-muted-foreground">{config.innerMatWidth}mm</span>
                        </div>
                        <Slider
                          value={[config.innerMatWidth]}
                          onValueChange={([v]) => setConfig({ ...config, innerMatWidth: v })}
                          min={5}
                          max={30}
                          step={5}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Glazing Options */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Glazing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Select 
                value={config.glazingType} 
                onValueChange={(v) => setConfig({ ...config, glazingType: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard Glass</SelectItem>
                  <SelectItem value="non-reflective">Non-Reflective Glass</SelectItem>
                  <SelectItem value="museum">Museum Glass (UV Protection)</SelectItem>
                  <SelectItem value="acrylic">Acrylic (Lightweight)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                {config.glazingType === "museum" && "Best for valuable pieces - 99% UV protection"}
                {config.glazingType === "non-reflective" && "Reduces glare for optimal viewing"}
                {config.glazingType === "standard" && "Classic clear glass finish"}
                {config.glazingType === "acrylic" && "Shatter-resistant, ideal for shipping"}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Configuration Summary */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-4">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium mb-2">Current Configuration Summary</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Frame: {FRAME_COLORS.find(f => f.id === config.frameColor)?.name} ({config.frameWidth}mm)</li>
                <li>• Mat: {MAT_COLORS.find(m => m.id === config.matColor)?.name} {config.matColor !== "none" && `(${config.matWidth}mm)`}</li>
                {config.hasDoubleMat && (
                  <li>• Inner Mat: {MAT_COLORS.find(m => m.id === config.innerMatColor)?.name} ({config.innerMatWidth}mm)</li>
                )}
                <li>• Glazing: {config.glazingType.charAt(0).toUpperCase() + config.glazingType.slice(1).replace("-", " ")}</li>
              </ul>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground mb-1">Use this configuration to</p>
              <p className="text-sm font-medium">Get quotes for all print sizes</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FrameBuilderTab;
